import { createSlice } from "@reduxjs/toolkit";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import {
  getAllReviews,
  approveReview,
  rejectReview,
  deleteReview,
} from "../../../api/reviewsAPI";

export const statusColor = {
  PENDING: "warning",
  APPROVED: "success",
  REJECTED: "error",
};

export const reviewsSlice = createSlice({
  name: "reviews",
  initialState: {
    reviews: [],
    status: "idle",
    pagination: {},
    page: 0,
    rowsPerPage: 10,
    sort: "desc",
    sortBy: "createdAt",
    statusFilter: null,
    parkingAreaId: null,
    filter: null,
    search: null,
    selectedId: null,
    selectedReview: null,
    detailModalOpen: false,
    deleteModalOpen: false,
    successMessage: "",
    errorMessage: "",
  },
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = action.payload;
    },
    setReviews: (state, action) => {
      state.reviews = action.payload;
      state.status = "success";
    },
    setSelectedId: (state, action) => {
      state.selectedId = action.payload;
      state.selectedReview = action.payload
        ? state.reviews.find((r) => r.id === action.payload) || null
        : null;
    },
    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
    },
    setSearch: (state, action) => {
      state.search = action.payload || null;
    },
    showDetailModal: (state, action) => {
      state.detailModalOpen = action.payload;
    },
    showDeleteModal: (state, action) => {
      state.deleteModalOpen = action.payload;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload;
    },
  },
});

export const {
  setPage,
  setRowsPerPage,
  setPagination,
  setReviews,
  setSelectedId,
  setStatusFilter,
  setSearch,
  showDetailModal,
  showDeleteModal,
  setSuccessMessage,
  setErrorMessage,
} = reviewsSlice.actions;

export const selectState = (state) => state.reviews;

export const handleGetReviews = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    const response = await getAllReviews(
      state.reviews.page,
      state.reviews.rowsPerPage,
      state.reviews.sort,
      state.reviews.sortBy,
      state.reviews.statusFilter,
      state.reviews.parkingAreaId,
      state.reviews.filter,
      state.reviews.search,
    );
    const { content, ...pageable } = response.data;
    dispatch(setPagination(pageable));
    dispatch(setReviews(content));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleApproveReview = (id) => async (dispatch) => {
  dispatch(showLoading());

  try {
    await approveReview(id);
    dispatch(showDetailModal(false));
    dispatch(setSuccessMessage("Review approved successfully!"));
    dispatch(handleGetReviews());
    dispatch(setSelectedId(null));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleRejectReview = (id) => async (dispatch) => {
  dispatch(showLoading());

  try {
    await rejectReview(id);
    dispatch(showDetailModal(false));
    dispatch(setSuccessMessage("Review rejected successfully!"));
    dispatch(handleGetReviews());
    dispatch(setSelectedId(null));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleDeleteReview = (id) => async (dispatch) => {
  dispatch(showLoading());

  try {
    await deleteReview(id);
    dispatch(showDeleteModal(false));
    dispatch(setSuccessMessage("Review deleted successfully!"));
    dispatch(handleGetReviews());
    dispatch(setSelectedId(null));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export default reviewsSlice.reducer;
